import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import { format, startOfDay, endOfDay, subDays, subMonths, subYears, addYears } from "date-fns";
import { enUS } from "date-fns/locale";
import { Box, ClickAwayListener, IconButton, InputAdornment, Paper, Popper, TextField } from "@mui/material";
import { CalendarToday } from "@mui/icons-material";

import { ActionButton } from "./ActionButton";

const DEFAULT_RANGE = [
  {
    startDate: startOfDay(subYears(new Date(), 1)),
    endDate: endOfDay(addYears(new Date(), 1)),
    key: "selection",
  },
];

const buildRange = (startDate, endDate) => [
  {
    startDate: startDate ? new Date(startDate) : new Date(),
    endDate: endDate ? new Date(endDate) : new Date(),
    key: "selection",
  },
];

interface IDateRangeInputProps {
  startDate?: Date | string;
  endDate?: Date | string;
  onDateRangeChange?: (ranges: any[]) => void;
  fullWidth?: boolean;
  style?: object;
}

/**
 * A date range picker that supports controlled and uncontrolled modes.
 * If both `startDate` and `endDate` are provided, the component is controlled by the parent.
 * Otherwise, it manages its own state internally.
 *
 * @component
 * @param {Object}          props
 * @param {Date|string}     [props.startDate]              - Start date of the range.
 * @param {Date|string}     [props.endDate]                - End date of the range.
 * @param {function}        [props.onDateRangeChange]      - Callback fired on Apply; receives the selected range array.
 * @param {boolean}         [props.fullWidth=false]        - Whether the input stretches to fill its parent.
 * @param {Object}          [props.style={}]               - Additional MUI sx styles for the trigger TextField.
 * IMPORTANT STYLING NOTE:
 * Base library styles are imported in: src/index.js
 * Custom theme overrides are defined in: src/main.css
 *
 * @returns {JSX.Element}
 */

export const DateRangeInput = ({
  startDate, // optional — parent-controlled start date
  endDate, // optional — parent-controlled end date
  onDateRangeChange = () => {},
  fullWidth = false,
  style = {},
}: IDateRangeInputProps) => {
  const isControlled = startDate != null && endDate != null;

  const initialRange = useMemo(
    () => (isControlled ? buildRange(startDate, endDate) : DEFAULT_RANGE),
    [isControlled, startDate, endDate],
  );

  const [range, setRange] = useState(initialRange);
  const [tempRange, setTempRange] = useState(initialRange);
  const [open, setOpen] = useState(false);

  const anchorRef = useRef(null);

  // Keep temporary picker range in sync with controlled props so the calendar
  // selection matches the externally managed start/end dates.
  useEffect(() => {
    if (isControlled) {
      setTempRange(buildRange(startDate, endDate));
    }
  }, [isControlled, startDate, endDate]);

  useEffect(() => {
    if (!isControlled) {
      onDateRangeChange([initialRange[0]]);
    }
  }, [isControlled, onDateRangeChange, initialRange]);

  // If parent is controlling dates, derive display range from props; otherwise use local state
  const displayRange = isControlled ? buildRange(startDate, endDate) : range;

  const customStaticRanges = createStaticRanges([
    {
      label: "Today",
      range: () => ({
        startDate: startOfDay(new Date()),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Yesterday",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 1)),
        endDate: endOfDay(subDays(new Date(), 1)),
      }),
    },
    {
      label: "Last 7 Days",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 6)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 30 Days",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 29)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 3 Months",
      range: () => ({
        startDate: startOfDay(subMonths(new Date(), 3)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 6 Months",
      range: () => ({
        startDate: startOfDay(subMonths(new Date(), 6)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last 1 Year",
      range: () => ({
        startDate: startOfDay(subYears(new Date(), 1)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Last & Next 1 Year",
      range: () => ({
        startDate: startOfDay(subYears(new Date(), 1)),
        endDate: endOfDay(addYears(new Date(), 1)),
      }),
    },
  ]);

  const formattedRange = `${format(displayRange[0].startDate, "dd MMM yy")} - ${format(displayRange[0].endDate, "dd MMM yy")}`;

  const handleReset = () => {
    setTempRange(isControlled ? buildRange(startDate, endDate) : range);
    setOpen(false);
  };

  const handleApply = () => {
    if (!isControlled) {
      setRange(tempRange);
    }
    setOpen(false);
    onDateRangeChange(tempRange);
  };

  return (
    <Box
      ref={anchorRef}
      sx={{
        display: fullWidth ? "block" : "inline-block",
        width: fullWidth ? "100%" : "auto",
      }}
    >
      <TextField
        value={formattedRange}
        size="small"
        sx={{
          ...style,
        }}
        slotProps={{
          input: {
            readOnly: true,
            onClick: () => setOpen((prev) => !prev),
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0 }}>
                <IconButton
                  size="small"
                  sx={{ p: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen((prev) => !prev);
                  }}
                >
                  <CalendarToday fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        fullWidth={fullWidth}
      />

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              borderRadius: 2,
              overflow: "visible",
              p: 1,
            }}
          >
            <DateRangePicker
              locale={enUS}
              editableDateInputs
              onChange={(item) => setTempRange([item.selection])}
              showSelectionPreview
              moveRangeOnFirstSelection={false}
              months={1}
              ranges={tempRange}
              direction="horizontal"
              staticRanges={customStaticRanges}
              inputRanges={[]}
            />

            {/* Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mr: 2.5, mb: 2.5 }}>
              <ActionButton buttonText="Cancel" onButtonClick={handleReset} variant="outlined" size="small" />
              <ActionButton buttonText="Apply" onButtonClick={handleApply} size="small" />
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
